import {
  CheckCircle,
  CheckCircle2,
  ClipboardCheck,
  Download,
  FileSearch,
  FileText,
  Info,
  MapPin,
  Pause,
  Play,
  Printer,
  XCircle,
  ArrowLeft,
  Zap,
  AlertCircle,
  ArrowRight,
  FileCheck,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { Button } from "@shared/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@shared/ui/card";
import { Checkbox } from "@shared/ui/checkbox";
import { Label } from "@shared/ui/label";
import { Textarea } from "@shared/ui/textarea";

import SetAccountAndPlan from "@features/cx/consumerOnboarding/components/details/detailSteps/approval/selectPlan";
import SelectServicePremise from "@features/cx/consumerOnboarding/components/details/detailSteps/approval/selectPremise";
import {
  usePremises,
  usePremiseInfo,
} from "@features/cx/consumerOnboarding/hooks";
import { TimelineTab } from "@features/cx/shared/components/tabs/TimelineTab";
import { ActionButton } from "@features/cx/shared/types/consumerRequest";
import { useToast } from "@shared/hooks/use-toast";
import { useFormCompletionTracker } from "@shared/hooks/useFormCompletionTracker";
import { Badge } from "@shared/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@shared/ui/table";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import {
  useConsumerDetails,
  useDownloadReport,
  useServiceOrderByConsumer,
  useUpdateConsumerApprovalStatus,
  useUpdateStatus,
} from "../../hooks";
import ApplicationHeader from "./ApplicationHeader";
import { useWorkflow } from "./WorkflowContext";
import WorkflowProgress from "./WorkflowProgress";

const ApprovalPage = () => {
  const { consumerId: consumerId } = useParams<{ consumerId: string }>();
  const remoteUtilityId = getRemoteUtilityId();
  const {
    application,
    workflowSteps,
    moveToNextStep,
    updateApplicationStatus,
    toggleInspectionRequirement,
  } = useWorkflow();
  const { data: consumerDetailsData } = useConsumerDetails({
    remote_utility_id: remoteUtilityId,
    consumer_id: consumerId,
    is_detail_info: true,
  });
  const [approvalDecision, setApprovalDecision] = useState<
    "approve" | "reject" | null
  >(null);

  const currentStatusDisplay =
    application?.progressStatusDisplay ||
    application?.statusDisplay ||
    consumerDetailsData?.result?.progressStatusDisplay ||
    consumerDetailsData?.result?.statusDisplay ||
    "";

  const normalizedStatus = String(currentStatusDisplay)
    .toUpperCase()
    .replace(/_/g, " ");

  const isInstallationCompletedOrActivation =
    normalizedStatus.includes("INSTALLATION COMPLETED") ||
    normalizedStatus.includes("ACTIVATION") ||
    normalizedStatus.includes("COMPLETED");

  const overallStep = isInstallationCompletedOrActivation
    ? 5
    : normalizedStatus.includes("INSTALLATION")
      ? 4
      : normalizedStatus.includes("APPROVED") ||
          normalizedStatus.includes("APPROVAL")
        ? 3
        : normalizedStatus.includes("SURVEY") ||
            normalizedStatus.includes("INSPECTION") ||
            normalizedStatus.includes("ACCEPTED")
          ? 2
          : 1;

  // 2 steps or more forward => Approval tab is read-only (non-editable)
  const isApprovalReadOnly = overallStep - 3 >= 2;

  const [isApproved, setIsApproved] = useState(
    application?.statusDisplay === "APPROVED" ||
      isApprovalReadOnly ||
      overallStep > 3,
  );

  const isActuallyApproved =
    application?.statusDisplay === "APPROVED" ||
    consumerDetailsData?.result?.statusDisplay === "APPROVED" ||
    isApprovalReadOnly ||
    overallStep > 3;

  const [justApproved, setJustApproved] = useState(false);

  useEffect(() => {
    if (
      application?.statusDisplay === "APPROVED" ||
      consumerDetailsData?.result?.statusDisplay === "APPROVED" ||
      isApprovalReadOnly ||
      overallStep > 3
    ) {
      setIsApproved(true);
    } else {
      setIsApproved(false);
    }
  }, [
    application?.statusDisplay,
    consumerDetailsData?.result?.statusDisplay,
    isApprovalReadOnly,
    overallStep,
  ]);

  console.log("debug approve", isApproved, application?.statusDisplay);
  const { data: premisesData } = usePremises();
  const [selectedUnitCode, setSelectedUnitCode] = useState<string>("");
  const [isUnitAdded, setIsUnitAdded] = useState<boolean>(false);
  const [approvalNotes, setApprovalNotes] = useState("");
  const [requirements, setRequirements] = useState({
    inspectionPassed: application?.additionalData?.isSiteInspection || false,
    paymentReceived: application?.additionalData?.kyc || false,
    technicalRequirements:
      application?.additionalData?.isPaymentApprove || false,
    legalRequirements: application?.additionalData?.isLegalReqMet || false,
  });
  const { startTracking, logTimeUntilStep, stopTracking } =
    useFormCompletionTracker("New Service Connection Details");
  const navigate = useNavigate();
  const { toast } = useToast();

  // Initialize the hooks
  const updateConsumerApprovalStatus = useUpdateConsumerApprovalStatus();
  const updateStatus = useUpdateStatus(Number(consumerId));
  const downloadReport = useDownloadReport();

  console.log("🔍 ApprovalPage - Hook parameters:", {
    remoteUtilityId,
    consumerId,
    tab_name: "Survey_Inprogress",
    application: !!application,
  });

  const {
    data: serviceOrderData,
    isLoading: isLoadingServiceOrder,
    error: serviceOrderError,
  } = useServiceOrderByConsumer({
    remote_utility_id: remoteUtilityId,
    consumer_id: consumerId,
    tab_name: "Survey_Inprogress",
  });

  console.log("🔍 ApprovalPage - Query result:", {
    serviceOrderData,
    isLoadingServiceOrder,
    serviceOrderError,
  });

  const servicePremiseLabel =
    consumerDetailsData?.result?.territoryData?.service?.premise;
  const billingPremiseLabel =
    consumerDetailsData?.result?.territoryData?.billing?.premise;

  const matchedServicePremise = premisesData?.result?.find(
    (p: any) => p.name === servicePremiseLabel,
  );
  const matchedBillingPremise = premisesData?.result?.find(
    (p: any) => p.name === billingPremiseLabel,
  );

  const servicePremiseId = matchedServicePremise?.code || "";
  const billingPremiseId = matchedBillingPremise?.code || "";

  const { data: servicePremiseInfo } = usePremiseInfo(servicePremiseId);
  const { data: billingPremiseInfo } = usePremiseInfo(billingPremiseId);
  const reportId =
    serviceOrderData?.result?.id || serviceOrderData?.result?.jobCardNo;

  const viewReport = useDownloadReport();

  useEffect(() => {
    if (application) {
      startTracking();
      setRequirements((prev) => ({
        ...prev,
        inspectionPassed: application.requiresInspection
          ? prev.inspectionPassed
          : true,
      }));
    }
  }, [application?.requiresInspection]);
  useEffect(() => {
    if (application?.additionalData) {
      setRequirements({
        inspectionPassed: application.additionalData.isSiteInspection || false,
        paymentReceived: application.additionalData.kyc || false,
        technicalRequirements:
          application.additionalData.isPaymentApprove || false,
        legalRequirements: application.additionalData.isLegalReqMet || false,
      });
    }
  }, [application?.additionalData]);

  // Auto-set isUnitAdded and isApproved when consumerDetailsData contains existing premise or plan info
  useEffect(() => {
    const res = consumerDetailsData?.result;
    if (res) {
      const unitCodeFromRes =
        res?.unitInfo?.basicInfo?.code ||
        res?.unitInfo?.code ||
        res?.unitCode ||
        res?.addressData?.service?.UNIT ||
        res?.address_data?.service?.UNIT ||
        res?.territoryData?.service?.unit ||
        res?.territory_data?.service?.unit;

      const hasPremiseOrPlan =
        Boolean(unitCodeFromRes) ||
        Boolean(res?.planDetails) ||
        Boolean(res?.plan_details) ||
        Boolean(res?.remotePlanId) ||
        Boolean(res?.territoryData?.service?.premise) ||
        Boolean(res?.territory_data?.service?.premise);

      if (unitCodeFromRes) {
        setSelectedUnitCode(unitCodeFromRes);
      }

      if (hasPremiseOrPlan) {
        setIsUnitAdded(true);
      }

      const statusUpper = String(res.statusDisplay || "").toUpperCase();
      if (
        statusUpper.includes("APPROVED") ||
        statusUpper.includes("INSTALLATION") ||
        statusUpper.includes("ACTIVATION") ||
        statusUpper.includes("COMPLETED") ||
        hasPremiseOrPlan
      ) {
        setIsApproved(true);
      }
    }
  }, [consumerDetailsData]);

  // Map API response to inspection data format
  const inspectionData = serviceOrderData?.result
    ? {
        serviceOrderName: serviceOrderData.result.sop?.name || "N/A",
        description: `${serviceOrderData.result.sop?.sopType || ""} - ${
          serviceOrderData.result.sop?.sop_subtype || "N/A"
        }`,
        type: serviceOrderData.result?.sopTypeDisplay || "N/A",
        subType: serviceOrderData.result?.sopSubtypeDisplay || "N/A",
        createdOn: serviceOrderData.result.createdDate || "N/A",
        scheduledOn: serviceOrderData.result.expectedEndDate || "N/A",
        assignedOn: serviceOrderData.result.assignDate || "N/A",
        assignedTo: serviceOrderData?.result?.fieldForce?.fieldForceNo || "N/A",
        status: serviceOrderData.result.statusDisplay || "N/A",
        serviceAddress: serviceOrderData.result.consumerAddress || "N/A",
        jobCardNo: serviceOrderData.result.jobCardNo || "N/A",
        requestAccountNo: serviceOrderData.result.requestAccountNo || "N/A",
        mobileNumber: serviceOrderData.result.mobileNumber || "N/A",
        requestUsername: serviceOrderData.result.requestUsername || "N/A",
      }
    : null;

  // Determine if inspection is complete/passed based on status
  const isInspectionPassed =
    serviceOrderData?.result?.consumerStatus === 1 &&
    serviceOrderData?.result?.consumerStatusDisplay === "APPROVED";

  const hasInspectionData = Boolean(inspectionData);

  useEffect(() => {
    if (hasInspectionData) {
      setRequirements((prev) => {
        if (prev.inspectionPassed === isInspectionPassed) {
          return prev;
        }
        return {
          ...prev,
          inspectionPassed: isInspectionPassed,
        };
      });
    }
  }, [hasInspectionData, isInspectionPassed]);

  console.log("🔍 ApprovalPage - Component render check:", {
    hasApplication: !!application,
    applicationData: application,
  });

  if (!application) {
    console.log("⚠️ ApprovalPage - Early return due to missing application");
    return <div>Loading...</div>;
  }

  const allRequirementsMet = Object.values(requirements).every(Boolean);

  const handleViewReport = async () => {
    if (!reportId) {
      toast({
        title: "No report available",
        description: "Report ID not found for this inspection.",
        variant: "destructive",
      });
      return;
    }

    try {
      const blob = await viewReport.mutateAsync({
        remote_utility_id: remoteUtilityId,
        report_id: reportId,
      });

      const url = window.URL.createObjectURL(blob);
      window.open(url, "_blank");

      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 1000);
    } catch (error) {
      toast({
        title: "Error viewing report",
        description: "Failed to open the inspection report.",
        variant: "destructive",
      });
    }
  };

  const handleDownloadReport = async () => {
    if (!reportId) {
      toast({
        title: "No report available",
        description: "Report ID not found for this inspection.",
        variant: "destructive",
      });
      return;
    }

    try {
      await downloadReport.mutateAsync({
        remote_utility_id: remoteUtilityId,
        report_id: reportId,
      });

      toast({
        title: "Download started",
        description: "The inspection report is being downloaded.",
      });
    } catch (error) {
      toast({
        title: "Download failed",
        description: "Failed to download the inspection report.",
        variant: "destructive",
      });
    }
  };

  const handleApprove = async () => {
    if (!allRequirementsMet) {
      toast({
        title: "Cannot approve",
        description: "All requirements must be met before approval.",
        variant: "destructive",
      });
      return;
    }

    // if (approvalDecision !== "approve") {
    //   toast({
    //     title: "Approval decision required",
    //     description: "Please select 'Approve' to proceed with approval.",
    //     variant: "destructive",
    //   });
    //   return;
    // }

    try {
      const payload: any = {
        additional_data: {
          is_site_inspection: requirements.inspectionPassed,
          kyc: requirements.paymentReceived,
          is_payment_approve: requirements.technicalRequirements,
          is_legal_req_met: requirements.legalRequirements,
        },
      };

      await updateConsumerApprovalStatus.mutateAsync(
        {
          consumerId: consumerId,
          payload: payload,
        },
        {
          onSuccess: () => {
            logTimeUntilStep("Approval");
            setIsApproved(true);
            setJustApproved(true);
          },
        },
      );

      updateApplicationStatus("installation");
      moveToNextStep();

      toast({
        title: "Application approved",
        description:
          "The application has been approved and is ready for meter activation.",
      });

      // navigate(`/workflow/installation/${consumerId}/INSTALLATION INPROGRESS`);
    } catch (error) {
      console.error("Failed to approve application:", error);
      toast({
        title: "Approval failed",
        description: "Failed to approve the application. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleReject = async () => {
    if (approvalDecision !== "reject") {
      toast({
        title: "Rejection decision required",
        description: "Please select 'Reject' to proceed with rejection.",
        variant: "destructive",
      });
      return;
    }

    if (!approvalNotes.trim()) {
      toast({
        title: "Rejection reason required",
        description: "Please provide a reason for rejection in the notes.",
        variant: "destructive",
      });
      return;
    }

    try {
      const payload: any = {
        is_application_reject: 1,
        remark: approvalNotes.trim(),
      };

      await updateConsumerApprovalStatus.mutateAsync({
        consumerId: consumerId,
        payload: payload,
      });

      updateApplicationStatus("rejected");

      toast({
        title: "Application rejected",
        description: "The application has been rejected.",
        variant: "destructive",
      });

      navigate("/dashboard");
    } catch (error) {
      console.error("Failed to reject application:", error);
      toast({
        title: "Rejection failed",
        description: "Failed to reject the application. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleBack = () => {
    navigate(`/cx/consumer-onboarding/workflow/inspection/${consumerId}`);
  };

  const toggleInspection = (required: boolean) => {
    toggleInspectionRequirement(required);
    if (!required) {
      setRequirements((prev) => ({
        ...prev,
        inspectionPassed: true,
      }));
    }
  };

  const handleAcknowledge = () => {
    // update the status in the database
    const acknowledge_date = new Date().toISOString();
    updateStatus.mutate({
      remote_utility_id: remoteUtilityId,
      progress_status: "IN_PROGRESS",
      acknowledge_date,
    });
    toast({
      title: "Application acknowledged",
      description:
        "The application has been acknowledged and is now in progress.",
    });
  };

  const handlePutOnHold = () => {
    try {
      updateStatus.mutate({
        remote_utility_id: remoteUtilityId,
        progress_status: "HOLD",
      });

      toast({
        title: "Application on hold",
        description: "The application has been put on hold.",
      });
    } catch (error) {
      console.error("Error putting application on hold:", error);
      toast({
        title: "Error",
        description: "Failed to put application on hold. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleResume = () => {
    try {
      updateStatus.mutate({
        remote_utility_id: remoteUtilityId,
        progress_status: "IN_PROGRESS",
      });

      toast({
        title: "Application resumed",
        description: "The application has been resumed and is now in progress.",
      });
    } catch (error) {
      console.error("Error resuming application:", error);
      toast({
        title: "Error",
        description: "Failed to resume application. Please try again.",
        variant: "destructive",
      });
    }
  };

  const actionButtons: ActionButton[] = [
    {
      id: "acknowledge",
      label: "Acknowledge",
      icon: CheckCircle,
      variant: "default",
      onClick: handleAcknowledge,
      condition: (status) =>
        application.progressStatusDisplay === "Acknowledge Pending",
    },
    {
      id: "hold",
      label: "Put on Hold",
      icon: Pause,
      variant: "outline",
      onClick: handlePutOnHold,
      condition: (status) =>
        application.progressStatusDisplay === "In Progress",
    },
    {
      id: "resume",
      label: "Resume",
      icon: Play,
      variant: "default",
      onClick: handleResume,
      condition: (status) => application.progressStatusDisplay === "Hold",
    },
  ];

  const relevantActions = actionButtons.filter(
    (action) =>
      !action.condition || action.condition(application.progressStatusDisplay),
  );
  const isInspectionNotApproved =
    inspectionData?.status?.toLowerCase() !== "approved";
  const hasEmptyServiceOrderResult =
    serviceOrderData?.result &&
    Object.keys(serviceOrderData.result).length === 0;

  function getStatusClasses(status) {
    switch (status) {
      case "Acknowledge Pending":
        return "bg-yellow-100 text-yellow-800 border-yellow-300";
      case "In Progress":
        return "bg-green-100 text-green-800 border-green-300";
      case "Hold":
        return "bg-red-100 text-red-800 border-red-300";
      default:
        return "bg-gray-100 text-gray-800 border-gray-300";
    }
  }

  return (
    <>
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/cx/consumer-onboarding")}
            className="hover:bg-gray-100 rounded-full h-10 w-10"
          >
            <ArrowLeft className="h-6 w-6" />
          </Button>
          <h1 className="text-2xl font-bold">New Service Connection Details</h1>
        </div>
        <div className="flex gap-2">
          {relevantActions.map((action) => (
            <Button
              key={action.id}
              size="sm"
              variant={action.variant}
              onClick={action.onClick}
            >
              <action.icon className="h-4 w-4 mr-2" />
              {action.label}
            </Button>
          ))}
        </div>
      </div>

      <ApplicationHeader
        remote_utility_id={remoteUtilityId}
        is_detail_info={1}
      />

      <WorkflowProgress
        steps={workflowSteps}
        currentStepId={application.currentStep}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          {/* Only show inspection results if inspection was required AND performed AND data is available */}
          {application.requiresInspection &&
            !hasEmptyServiceOrderResult &&
            Object.keys(inspectionData || {}).length > 0 &&
            application.apiStatus.toLowerCase() !== "approval inprogress" && (
              <Card className="mb-6">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <ClipboardCheck className="h-5 w-5 text-workflow-blue" />
                    Inspection Results
                  </CardTitle>
                  <CardDescription>
                    Review inspection results and make a final approval decision
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {isLoadingServiceOrder ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="text-sm text-gray-500">
                        Loading inspection data...
                      </div>
                    </div>
                  ) : serviceOrderError ? (
                    <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-4">
                      <div className="flex items-start">
                        <XCircle className="h-5 w-5 text-red-500 mt-0.5 mr-2" />
                        <div>
                          <h3 className="font-medium">
                            Error Loading Inspection Data
                          </h3>
                          <p className="text-sm text-gray-600">
                            Unable to load inspection results. Please try
                            refreshing the page.
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div
                        className={`border rounded-md p-4 mb-4 ${
                          isInspectionPassed
                            ? "bg-green-50 border-green-200"
                            : "bg-yellow-50 border-yellow-200"
                        }`}
                      >
                        <div className="flex items-start">
                          {isInspectionPassed ? (
                            <CheckCircle className="h-5 w-5 text-workflow-green mt-0.5 mr-2" />
                          ) : (
                            <Info className="h-5 w-5 text-yellow-600 mt-0.5 mr-2" />
                          )}
                          <div>
                            <h3 className="font-medium">
                              {isInspectionPassed
                                ? "Inspection Completed Successfully"
                                : `Inspection Status: ${inspectionData.status}`}
                            </h3>
                            <p className="text-sm text-gray-600">
                              {isInspectionPassed
                                ? "Site inspection confirmed the location meets all technical requirements."
                                : "Please review the current inspection status and details below."}
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <Table className="text-sm">
                            <TableHeader>
                              <TableRow>
                                <TableHead
                                  colSpan={2}
                                  className="text-sm font-medium"
                                >
                                  Service Details
                                </TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              <TableRow>
                                <TableCell className="font-medium py-2">
                                  Service Order
                                </TableCell>
                                <TableCell className="py-2">
                                  {inspectionData.serviceOrderName}
                                </TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell className="font-medium py-2">
                                  SO Id
                                </TableCell>
                                <TableCell className="py-2">
                                  {inspectionData.jobCardNo}
                                </TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell className="font-medium py-2">
                                  Type
                                </TableCell>
                                <TableCell className="py-2">
                                  {inspectionData.type}
                                </TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell className="font-medium py-2">
                                  Sub Type
                                </TableCell>
                                <TableCell className="py-2">
                                  {inspectionData.subType}
                                </TableCell>
                              </TableRow>
                            </TableBody>
                          </Table>

                          <Table className="text-sm">
                            <TableHeader>
                              <TableRow>
                                <TableHead
                                  colSpan={2}
                                  className="text-sm font-medium"
                                >
                                  Schedule Information
                                </TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              <TableRow>
                                <TableCell className="font-medium py-2">
                                  Created On
                                </TableCell>
                                <TableCell className="py-2">
                                  {inspectionData.createdOn}
                                </TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell className="font-medium py-2">
                                  Scheduled On
                                </TableCell>
                                <TableCell className="py-2">
                                  {inspectionData.scheduledOn}
                                </TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell className="font-medium py-2">
                                  Assigned On
                                </TableCell>
                                <TableCell className="py-2">
                                  {inspectionData.assignedOn || "Not assigned"}
                                </TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell className="font-medium py-2">
                                  Assigned To
                                </TableCell>
                                <TableCell className="py-2">
                                  {inspectionData.assignedTo}
                                </TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell className="font-medium py-2">
                                  Status
                                </TableCell>
                                <TableCell className="py-2">
                                  <span
                                    className={`px-2 py-0.5 rounded-full text-xs ${
                                      isInspectionPassed
                                        ? "bg-green-100 text-green-800"
                                        : "bg-yellow-100 text-yellow-800"
                                    }`}
                                  >
                                    {inspectionData.status}
                                  </span>
                                </TableCell>
                              </TableRow>
                            </TableBody>
                          </Table>
                        </div>

                        {/* Report Action Buttons */}
                        {!isInspectionNotApproved && (
                          <div className="flex gap-2 pt-4">
                            <Button
                              variant="outline"
                              onClick={handleViewReport}
                              disabled={!reportId || viewReport.isPending}
                              className="flex items-center gap-2"
                            >
                              <FileSearch className="h-4 w-4" />
                              {viewReport.isPending
                                ? "Loading..."
                                : "View Report"}
                            </Button>
                            <Button
                              variant="outline"
                              onClick={handleDownloadReport}
                              disabled={!reportId || downloadReport.isPending}
                              className="flex items-center gap-2"
                            >
                              <Download className="h-4 w-4" />
                              {downloadReport.isPending
                                ? "Downloading..."
                                : "Download Report"}
                            </Button>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            )}

          {/* Show message when inspection was skipped */}

          {hasEmptyServiceOrderResult && (
            <Card className="h-full p-8">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Info className="h-5 w-5 text-blue-600" />
                  Inspection Skipped
                </CardTitle>
                <CardDescription>
                  Site inspection was skipped for this application
                </CardDescription>
              </CardHeader>
              <CardContent className="flex flex-col items-center justify-center h-full pb-12 pt-4">
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-8 max-w-lg text-center flex flex-col items-center">
                  <div className="bg-white p-3 rounded-full shadow-sm mb-4">
                    <Info className="h-8 w-8 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-blue-800">
                    Inspection Not Performed
                  </h3>
                  <p className="text-sm text-blue-700 mt-3 leading-relaxed">
                    The site inspection step was skipped for this application.
                    You can proceed with the approval decision based on the
                    available documentation and application details.
                  </p>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Approval Requirements</CardTitle>
              <CardDescription className="">
                All requirements must be met for approval
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {application.requiresInspection && (
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="inspectionPassed"
                      checked={requirements.inspectionPassed}
                      onCheckedChange={(checked) =>
                        setRequirements((prev) => ({
                          ...prev,
                          inspectionPassed: checked === true,
                        }))
                      }
                      disabled={
                        application?.additionalData?.isSiteInspection ===
                          true ||
                        isActuallyApproved ||
                        justApproved
                      }
                    />
                    <div className="grid">
                      <Label
                        htmlFor="inspectionPassed"
                        className="text-sm font-medium"
                      >
                        {inspectionData
                          ? "Site inspection passed"
                          : "Site inspection (skipped)"}
                      </Label>
                      <p className="text-xs text-gray-500">
                        {inspectionData
                          ? "Verify that all inspection points have been completed successfully"
                          : "Inspection was skipped for this application"}
                      </p>
                    </div>
                  </div>
                )}

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="paymentReceived"
                    checked={requirements.paymentReceived}
                    onCheckedChange={(checked) =>
                      setRequirements((prev) => ({
                        ...prev,
                        paymentReceived: checked === true,
                      }))
                    }
                    disabled={
                      application?.additionalData?.kyc === true ||
                      isActuallyApproved ||
                      justApproved
                    }
                  />
                  <div className="grid">
                    <Label
                      htmlFor="paymentReceived"
                      className="text-sm font-medium"
                    >
                      Payment received
                    </Label>
                    <p className="text-xs text-gray-500">
                      Confirm that all applicable fees have been paid in full
                    </p>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="technicalRequirements"
                    checked={requirements.technicalRequirements}
                    onCheckedChange={(checked) =>
                      setRequirements((prev) => ({
                        ...prev,
                        technicalRequirements: checked === true,
                      }))
                    }
                    disabled={
                      application?.additionalData?.isPaymentApprove === true ||
                      isActuallyApproved ||
                      justApproved
                    }
                  />
                  <div className="grid">
                    <Label
                      htmlFor="technicalRequirements"
                      className="text-sm font-medium"
                    >
                      Technical requirements met
                    </Label>
                    <p className="text-xs text-gray-500">
                      Verify that the installation meets all technical
                      specifications
                    </p>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="legalRequirements"
                    checked={requirements.legalRequirements}
                    onCheckedChange={(checked) =>
                      setRequirements((prev) => ({
                        ...prev,
                        legalRequirements: checked === true,
                      }))
                    }
                    disabled={
                      application?.additionalData?.isLegalReqMet === true ||
                      isActuallyApproved ||
                      justApproved
                    }
                  />
                  <div className="grid">
                    <Label
                      htmlFor="legalRequirements"
                      className="text-sm font-medium"
                    >
                      Legal requirements met
                    </Label>
                    <p className="text-xs text-gray-500">
                      Confirm compliance with all applicable regulations and
                      permits
                    </p>
                  </div>
                </div>

                {/* Approval Decision Section - moved here */}
                <div className="pt-6 mt-6 border-t">
                  <div className="space-y-4">
                    <div>
                      <Label
                        htmlFor="approvalNotes"
                        className="text-sm font-medium mb-2 block"
                      >
                        Approval Notes
                      </Label>
                      <Textarea
                        id="approvalNotes"
                        placeholder="Add notes regarding your decision..."
                        value={approvalNotes}
                        onChange={(e) => setApprovalNotes(e.target.value)}
                      />
                    </div>

                    <div className="flex flex-col gap-2 pt-2">
                      <Button
                        onClick={handleApprove}
                        disabled={
                          updateConsumerApprovalStatus.isPending ||
                          isActuallyApproved ||
                          justApproved ||
                          !allRequirementsMet ||
                          ![
                            "survey inprogress",
                            "survey completed",
                            "approval inprogress",
                          ].includes(
                            application?.apiStatus?.toLowerCase() || "",
                          ) ||
                          application.progressStatusDisplay === "Hold" ||
                          (isInspectionNotApproved &&
                            !hasEmptyServiceOrderResult)
                        }
                        className="w-full"
                      >
                        {updateConsumerApprovalStatus.isPending &&
                        approvalDecision === "approve"
                          ? "Approving..."
                          : "Approve Application"}
                      </Button>

                      <Button
                        variant="outline"
                        className="border-workflow-red text-workflow-red hover:bg-red-50 w-full"
                        onClick={handleReject}
                        disabled={
                          updateConsumerApprovalStatus.isPending ||
                          isActuallyApproved ||
                          justApproved ||
                          !["survey inprogress", "survey completed"].includes(
                            application?.apiStatus?.toLowerCase() || "",
                          ) ||
                          application.progressStatusDisplay === "Hold" ||
                          (isInspectionNotApproved &&
                            !hasEmptyServiceOrderResult)
                        }
                      >
                        {updateConsumerApprovalStatus.isPending &&
                        approvalDecision === "reject"
                          ? "Rejecting..."
                          : "Reject Application"}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
      {/* Main Grid for Content and Timeline */}
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-6 mt-6">
        {/* Left Content Column (10 columns) */}
        <div className="xl:col-span-8 space-y-4">
          {/* Premise, Plan and Payment Details - Read Only View */}
          {consumerDetailsData?.result?.paymentLastData?.isPlanPayment ===
            true && (
            <div className="space-y-3">
              {/* Service Premise */}
              {consumerDetailsData?.result?.territoryData?.service?.premise && (
                <div className="p-6 bg-green-50 border border-green-200 rounded-lg shadow-sm">
                  <div className="flex items-center gap-2 border-b border-green-200 pb-3 mb-4">
                    <MapPin className="h-5 w-5 text-green-700" />
                    <div>
                      <p className="text-xs text-green-600 font-medium uppercase tracking-wider">
                        Service Premise
                      </p>
                      <p className="text-lg font-bold text-green-800">
                        {
                          consumerDetailsData.result.territoryData.service
                            .premise
                        }
                      </p>
                    </div>
                  </div>

                  {/* Grid Details */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-4">
                    <div>
                      <label className="text-xs font-semibold text-green-700 uppercase tracking-wide">
                        City
                      </label>
                      <p className="text-lg font-bold text-green-900 mt-1">
                        {matchedServicePremise?.hierarchyNames?.state ||
                          consumerDetailsData.result.territoryData.service
                            .state ||
                          "N/A"}
                      </p>
                    </div>

                    <div>
                      <label className="text-xs font-semibold text-green-700 uppercase tracking-wide">
                        Area
                      </label>
                      <p className="text-lg font-bold text-green-900 mt-1">
                        {matchedServicePremise?.hierarchyNames?.area ||
                          consumerDetailsData.result.territoryData.service
                            .area ||
                          "N/A"}
                      </p>
                    </div>

                    <div>
                      <label className="text-xs font-semibold text-green-700 uppercase tracking-wide">
                        Sub Area
                      </label>
                      <p className="text-lg font-bold text-green-900 mt-1">
                        {matchedServicePremise?.hierarchyNames?.subArea ||
                          consumerDetailsData.result.territoryData.service
                            .sub_area ||
                          "N/A"}
                      </p>
                    </div>

                    <div>
                      <label className="text-xs font-semibold text-green-700 uppercase tracking-wide">
                        Route Name
                      </label>
                      <p className="text-lg font-bold text-green-900 mt-1">
                        {servicePremiseInfo?.result?.mxData?.routeInfo
                          ?.routeName || "N/A"}
                      </p>
                    </div>

                    <div className="md:col-span-2">
                      <label className="text-xs font-semibold text-green-700 uppercase tracking-wide">
                        Billing Cycle Name
                      </label>
                      <p className="text-lg font-bold text-green-900 mt-1">
                        {servicePremiseInfo?.result?.bxData?.billCycleName ||
                          "N/A"}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Billing Premise */}
              {consumerDetailsData?.result?.territoryData?.billing?.premise && (
                <div className="p-6 bg-blue-50 border border-blue-200 rounded-lg shadow-sm">
                  <div className="flex items-center gap-2 border-b border-blue-200 pb-3 mb-4">
                    <MapPin className="h-5 w-5 text-blue-700" />
                    <div>
                      <p className="text-xs text-blue-600 font-medium uppercase tracking-wider">
                        Billing Premise
                      </p>
                      <p className="text-lg font-bold text-blue-800">
                        {
                          consumerDetailsData.result.territoryData.billing
                            .premise
                        }
                      </p>
                    </div>
                  </div>

                  {/* Grid Details */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-4">
                    <div>
                      <label className="text-xs font-semibold text-blue-700 uppercase tracking-wide">
                        City
                      </label>
                      <p className="text-lg font-bold text-blue-900 mt-1">
                        {matchedBillingPremise?.hierarchyNames?.state ||
                          consumerDetailsData.result.territoryData.billing
                            .state ||
                          "N/A"}
                      </p>
                    </div>

                    <div>
                      <label className="text-xs font-semibold text-blue-700 uppercase tracking-wide">
                        Area
                      </label>
                      <p className="text-lg font-bold text-blue-900 mt-1">
                        {matchedBillingPremise?.hierarchyNames?.area ||
                          consumerDetailsData.result.territoryData.billing
                            .area ||
                          "N/A"}
                      </p>
                    </div>

                    <div>
                      <label className="text-xs font-semibold text-blue-700 uppercase tracking-wide">
                        Sub Area
                      </label>
                      <p className="text-lg font-bold text-blue-900 mt-1">
                        {matchedBillingPremise?.hierarchyNames?.subArea ||
                          consumerDetailsData.result.territoryData.billing
                            .sub_area ||
                          "N/A"}
                      </p>
                    </div>

                    <div>
                      <label className="text-xs font-semibold text-blue-700 uppercase tracking-wide">
                        Route Name
                      </label>
                      <p className="text-lg font-bold text-blue-900 mt-1">
                        {billingPremiseInfo?.result?.mxData?.routeInfo
                          ?.routeName || "N/A"}
                      </p>
                    </div>

                    <div className="md:col-span-2">
                      <label className="text-xs font-semibold text-blue-700 uppercase tracking-wide">
                        Billing Cycle Name
                      </label>
                      <p className="text-lg font-bold text-blue-900 mt-1">
                        {billingPremiseInfo?.result?.bxData?.billCycleName ||
                          "N/A"}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Plan Details */}
              {consumerDetailsData?.result?.planDetails?.planName && (
                <div className="bg-white rounded-lg border-2 shadow-md relative">
                  {/* Selected Badge */}
                  <div className="absolute -top-3 left-4 bg-blue-500 text-white px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1 shadow-sm">
                    <CheckCircle2 className="w-3 h-3" />
                    Selected Plan
                  </div>

                  {/* Header */}
                  <div className="p-4 border-b border-gray-200 bg-blue-50 mt-3">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                        <FileText className="w-5 h-5 text-blue-600" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {consumerDetailsData.result.planDetails.planName}
                        </h3>
                        {consumerDetailsData.result.planDetails.description && (
                          <p className="text-sm text-gray-600 mt-1">
                            {consumerDetailsData.result.planDetails.description}
                          </p>
                        )}
                        <span className="inline-block mt-2 text-xs font-medium text-green-700 bg-green-100 px-2 py-1 rounded">
                          {consumerDetailsData.result.planDetails
                            .billingFrequencyDisplay || "N/A"}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-4 space-y-4">
                    {/* Utility Services */}
                    <div>
                      <h4 className="text-sm font-semibold text-gray-900 mb-2">
                        Utility Services
                      </h4>
                      {consumerDetailsData.result.planDetails.utilityService &&
                      consumerDetailsData.result.planDetails.utilityService
                        .length > 0 ? (
                        <div className="space-y-2">
                          {consumerDetailsData.result.planDetails.utilityService.map(
                            (service, index) => (
                              <div
                                key={index}
                                className="flex items-center justify-between bg-green-50 border border-green-200 rounded px-3 py-2"
                              >
                                <span className="text-sm text-gray-900 font-medium">
                                  {service}
                                </span>
                              </div>
                            ),
                          )}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500">
                          No services available
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Payment Details Card */}
              {consumerDetailsData?.result?.paymentLastData?.isPlanPayment && (
                <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
                  {/* Header */}
                  <div className="bg-blue-50 border-b border-blue-200 p-4">
                    <div className="flex items-start gap-3 justify-between">
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0">
                          <CheckCircle2 className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <h2 className="text-lg font-semibold text-blue-900">
                            Payment Details
                          </h2>
                          <p className="text-sm text-blue-700 mt-0.5">
                            Receipt #
                            {consumerDetailsData?.result?.paymentLastData
                              ?.transactionId || "N/A"}
                          </p>
                        </div>
                      </div>

                      <Button
                        onClick={() =>
                          navigate(`/cx/payments/success`, {
                            state: {
                              // Pass the payment data from consumerDetailsData
                              result:
                                consumerDetailsData?.result?.paymentLastData,
                              formData: {
                                // Map the data to match what the success page expects
                                customerInfo: {
                                  firstName:
                                    consumerDetailsData?.result?.firstName,
                                  lastName:
                                    consumerDetailsData?.result?.lastName,
                                  email: consumerDetailsData?.result?.email,
                                  contactNumber:
                                    consumerDetailsData?.result?.contactNumber,
                                  consumerNo:
                                    consumerDetailsData?.result?.consumerNo,
                                },
                                paymentMethod:
                                  consumerDetailsData?.result?.paymentLastData
                                    ?.paymentModeDisplay,
                                amount:
                                  consumerDetailsData?.result?.paymentLastData
                                    ?.amount,
                                // Add any other fields the success page needs
                              },
                            },
                          })
                        }
                        className="flex items-center gap-2 justify-center border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                        variant="outline"
                      >
                        <Printer className="w-4 h-4" />
                        View Receipt
                      </Button>
                    </div>
                  </div>

                  {/* Payment Content */}
                  <div className="p-4 space-y-4">
                    {/* Payment Method and Amount Paid Row */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Payment Method */}
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                        <p className="text-xs font-medium text-gray-600 mb-1">
                          Payment Method
                        </p>
                        <p className="text-base font-semibold text-gray-900">
                          {consumerDetailsData.result.payment
                            ?.paymentModeDisplay || "N/A"}
                        </p>
                      </div>

                      {/* Amount Paid */}
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                        <p className="text-xs font-medium text-gray-600 mb-1">
                          Amount Paid
                        </p>
                        <p className="text-base font-semibold text-gray-900">
                          {consumerDetailsData.result.paymentLastData.amount ||
                            "N/A"}
                        </p>
                      </div>
                    </div>

                    {/* Payment Date and Status */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Payment Date */}
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                        <p className="text-xs font-medium text-gray-600 mb-1">
                          Payment Date
                        </p>
                        <p className="text-base font-semibold text-gray-900">
                          {consumerDetailsData.result.paymentLastData
                            .paymentDate || "N/A"}
                        </p>
                      </div>

                      {/* Status */}
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                        <p className="text-xs font-medium text-gray-600 mb-1">
                          Status
                        </p>
                        <p className="text-base font-semibold text-gray-900">
                          {consumerDetailsData.result.paymentLastData.status ||
                            "N/A"}
                        </p>
                      </div>
                    </div>

                    {/* Fee Breakdown */}
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                      <h3 className="text-sm font-semibold text-gray-900 mb-3">
                        Fee Breakdown
                      </h3>

                      <div className="space-y-2">
                        {/* Processing Fees */}
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-700">Processing Fees</span>
                          <span className="text-gray-900 font-medium">
                            {consumerDetailsData.result.planDetails?.processingFees?.toFixed(
                              2,
                            ) || "N/A"}
                          </span>
                        </div>

                        {/* Connection Fee */}
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-700">Connection Fee</span>
                          <span className="text-gray-900 font-medium">
                            {consumerDetailsData.result.planDetails?.connectionFee?.toFixed(
                              2,
                            ) || "N/A"}
                          </span>
                        </div>

                        {/* Security Deposit */}
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-700">
                            Security Deposit
                          </span>
                          <span className="text-gray-900 font-medium">
                            {consumerDetailsData.result.planDetails?.securityDeposit?.toFixed(
                              2,
                            ) || "N/A"}
                          </span>
                        </div>

                        {/* Total */}
                        <div className="pt-2 border-t border-gray-300 flex items-center justify-between">
                          <span className="text-sm font-semibold text-gray-900">
                            Total Paid
                          </span>
                          <span className="text-base font-bold text-gray-900">
                            {consumerDetailsData.result.paymentLastData
                              .amount || "N/A"}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="p-4 border-t flex justify-end">
                    <Button
                      onClick={() =>
                        navigate(
                          `/cx/consumer-onboarding/workflow/installation/${consumerId}/INSTALLATION%20INPROGRESS`,
                        )
                      }
                      className="w-full"
                    >
                      Continue to Installation
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {(isApproved ||
            isUnitAdded ||
            Boolean(
              consumerDetailsData?.result?.territoryData?.service?.premise,
            ) ||
            Boolean(consumerDetailsData?.result?.planDetails)) && (
            <div className="space-y-4">
              <SelectServicePremise
                data={premisesData}
                consumerId={consumerId}
                consumerDetailsData={consumerDetailsData}
                isReadOnly={isApprovalReadOnly}
                onPremiseSelect={(premises, locationDetails, serviceUnits) => {
                  if (serviceUnits && serviceUnits.length > 0) {
                    setSelectedUnitCode(serviceUnits[0]);
                  } else {
                    setSelectedUnitCode("");
                  }
                }}
                onUnitAdded={(added) => setIsUnitAdded(added)}
              />
              {isUnitAdded && (
                <SetAccountAndPlan
                  consumerDetailsData={consumerDetailsData}
                  selectedUnitCode={selectedUnitCode}
                  isReadOnly={isApprovalReadOnly}
                />
              )}
            </div>
          )}
        </div>

        {/* Right Timeline Column (4 columns) */}
        <div className="xl:col-span-4">
          {/* Only show timeline if we have the necessary IDs */}
          {consumerId && (
            <TimelineTab
              remoteUtilityId={remoteUtilityId}
              entityId={consumerId}
              actionType={"nsc"}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default ApprovalPage;
